import { Container, Graphics, Rectangle, Renderer, Sprite, Texture } from "pixi.js"
import { RootEdge, rootNoise } from "./tree_root_fringe"

// The forest dirt's soil ramp, darkest first (packages/tools/src/dirt/gen.py SOIL; the LPC dirt pair is
// 3 and 4). The mound is drawn one step darker on the damp edge soil and one lighter on the trodden
// core, as the soil itself is, so it never shows as a band of another colour.
const SOIL = [0x73503a, 0x7b563c, 0x80593d, 0x865e40, 0x926c42, 0x97714a, 0x9b744c]
const REACH = 4 // how far the mound slopes out past the trunk

export interface SoilPixel { x: number; y: number; color: number }

/** The trunk's foot: the widest run of bark columns at the bottom of the tree. Spruces (a skirt of
 * needles to the ground) and old LPC trees (a baked shadow under the trunk) have none. */
export const rootTrunk = (edges: RootEdge[]): { left: number; right: number; bottom: number } | undefined => {
    if (!edges.length) return undefined
    const bottom = Math.max(...edges.map(edge => edge.y))
    const bark = edges.filter(edge => edge.bark && edge.y >= bottom - 4).map(edge => edge.x).sort((a, b) => a - b)
    let best: { left: number; right: number } | undefined
    for (let i = 0, start = 0; i < bark.length; i++) {
        if (i + 1 < bark.length && bark[i + 1] === bark[i] + 1) continue
        if (!best || bark[i] - bark[start] > best.right - best.left) best = { left: bark[start], right: bark[i] }
        start = i + 1
    }
    return best && best.right - best.left >= 4 ? { ...best, bottom } : undefined
}

/**
 * Soil heaped against the trunk's foot, so a tree on bare dirt stands IN it, as the grass fringe
 * does on grass. It covers the hard outline under the trunk with lumps 4-7px wide and 2-4px high,
 * sloping away past the trunk's sides; lit along the top from the upper left, no line under it, so it
 * runs into the ground.
 */
export const rootSoilMound = (edges: RootEdge[], zone: number): SoilPixel[] => {
    const trunk = rootTrunk(edges)
    if (!trunk) return []
    const { left, right, bottom } = trunk
    const heights = new Map<number, number>()
    for (let x = left - REACH, lump = 0; x <= right + REACH; lump++) {
        const width = 4 + Math.floor(rootNoise(x, lump, 101) * 4)
        const peak = [2, 3, 3, 4][Math.floor(rootNoise(x, lump, 103) * 4)]
        for (let i = 0; i < width; i++) {
            const inside = Math.min(x + i - left, right - (x + i))
            const slope = Math.max(0, Math.min(1, (inside + REACH) / (REACH + 1.5)))
            const height = peak * Math.sin(Math.PI * (i + 0.5) / width) ** 0.4 * slope
            heights.set(x + i, Math.max(heights.get(x + i) ?? 0, height))
        }
        x += width - 1 - Math.floor(rootNoise(x, lump, 107) * 2)
    }
    const lowest = new Map(edges.map(edge => [edge.x, edge.y]))
    const soil = (step: number) => SOIL[Math.max(0, Math.min(SOIL.length - 1, step + zone - 1))]
    const pixels: SoilPixel[] = []
    heights.forEach((raw, x) => {
        const height = Math.round(raw)
        if (height <= 0) return
        const foot = x >= left && x <= right ? lowest.get(x) ?? bottom : bottom
        const top = foot + 2 - height
        for (let y = top; y <= foot + 1; y++) {
            let step: number
            if (y === top) {
                const lit = Math.round(heights.get(x - 1) ?? 0) <= height
                step = lit ? (rootNoise(x, y, 3) < 0.25 ? 6 : 5) : 4
            } else {
                const mottle = rootNoise(x, y, 9)
                step = mottle < 0.4 ? 3 : mottle < 0.9 ? 4 : 2
            }
            pixels.push({ x, y, color: soil(step) })
        }
    })
    return pixels
}

// Shared by every tree of the same variant on the same soil zone, baked once like the grass fringe.
type TextureBaker = Pick<Renderer, "generateTexture">
const baked = new WeakMap<Texture, Map<number, { renderer: TextureBaker; texture: Texture }>>()
export const treeRootSoilTexture = (tree: Texture, edges: RootEdge[], zone: number, renderer: TextureBaker): Texture => {
    const cached = baked.get(tree)?.get(zone)
    if (cached?.renderer === renderer && !cached.texture.baseTexture.destroyed) return cached.texture
    const mound = rootSoilMound(edges, zone)
    if (!mound.length) return tree
    const surface = new Container()
    surface.addChild(new Sprite(tree))
    const soil = new Graphics()
    surface.addChild(soil)
    for (const pixel of mound) {
        soil.beginFill(pixel.color)
        soil.drawRect(pixel.x, pixel.y, 1, 1)
        soil.endFill()
    }
    const texture = renderer.generateTexture(surface, { region: new Rectangle(0, 0, tree.width, tree.height) })
    surface.destroy({ children: true })
    if (!baked.has(tree)) baked.set(tree, new Map())
    baked.get(tree)!.set(zone, { renderer, texture })
    return texture
}
